import React, {useEffect, useRef, useState, useCallback, useMemo} from 'react'
import Select from 'react-select'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {TextInput} from '../../../../components/inputs/TextInput'
import {
  ModeleCentre,
  ModeleEditCentre, ModeleEditEquipe,
  ModeleEditProvenance,
  ModeleEditRole,
  ModeleEditSource, ModeleEquipe,
  ModeleProvenance,
  ModeleSource,
  ModeleUtilisateur, ModeleValueLabel,
} from '../../../../components/Models'
import {SelectInput} from '../../../../components/inputs/SelectInput'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import {
  getAllServiceRattachements,
  getCentres, getEquipeUtilisateur,
  getProvenancePiece,
  getRoles,
  getSources,
  validatePseudoEmail,
  validatePseudoEmailEdit,
} from '../../../../components/requests'

const API_URL = process.env.REACT_APP_API_URL
const REGISTER_PDP_URL = `${API_URL}/utilisateur/register-pdp`
const pdp_url = `${API_URL}/uploads/pdp/`

/* -------------------------------------------------------------------------- */
/* Schéma de validation                                                       */
/* -------------------------------------------------------------------------- */

const MSG = {
  nomRequis: 'Le nom est requis',
  prenomRequis: 'Le prénom est requis',
  emailRequis: "L'adresse mail est requise",
  emailInvalide: "L'adresse mail n'est pas valide",
  emailExiste: "L'adresse mail existe déjà",
  pseudoRequis: "Le nom d'utilisateur est requis",
  pseudoExiste: "Le nom d'utilisateur existe déjà",
  mdpRequis: 'Le mot de passe est requis',
  mdpMin: 'Le mot de passe doit contenir au moins 8 caractères',
  confirmationRequise: 'La confirmation de mot de passe est requise',
  confirmationDifferente: 'Les deux mots de passe doivent correspondre',
}

const emailFormatSchema = Yup.string().email()

// Transforme une réponse d'API en options pour react-select / SelectInput
const toOptions = (items: any[] = [], valueKey: string, labelKey: string) =>
  items.map((item) => ({value: item[valueKey], label: item[labelKey]}))

const INPUT_CLASS = 'form-control form-control-lg form-control-solid mb-3 mb-lg-0'
const SELECT_CLASS = 'react-select-styled react-select-solid react-select-lg'

/* -------------------------------------------------------------------------- */
/* Champ mot de passe (évite de dupliquer le même bloc deux fois)              */
/* -------------------------------------------------------------------------- */

type PasswordFieldProps = {
  label: string
  name: 'password' | 'passwordConfirmation'
  placeholder: string
  required: boolean
  formik: any
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void
}

const PasswordField = ({label, name, placeholder, required, formik, onBlur}: PasswordFieldProps) => {
  const [visible, setVisible] = useState(false)
  const error = formik.touched[name] && formik.errors[name]

  return (
    <div className='row mb-6'>
      <label className={`col-lg-4 col-form-label ${required ? 'required' : ''} fw-bold fs-6`}>
        {label}
      </label>

      <div className='col-lg-8 fv-row'>
        <div className='position-relative'>
          <input
            placeholder={placeholder}
            type={visible ? 'text' : 'password'}
            autoComplete='new-password'
            {...formik.getFieldProps(name)}
            className={INPUT_CLASS}
            onBlur={onBlur}
          />
          <i
            className={`ki-outline ${visible ? 'ki-eye' : 'ki-eye-slash'} position-absolute fs-2 cursor-pointer`}
            style={{
              top: '50%',
              right: '15px',
              transform: 'translateY(-50%)',
              transition: 'right ease 0.3s',
            }}
            onClick={() => setVisible((v) => !v)}
          ></i>
        </div>
        {error && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>{error as string}</div>
          </div>
        )}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Formulaire                                                                  */
/* -------------------------------------------------------------------------- */

const UtilisateurForm = (props) => {
  const navigate = useNavigate()
  const utilisateurId = props.utilisateurId
  const isEdit = !!utilisateurId
  const [data] = useState<ModeleUtilisateur>(props.data)
  const [centreSelected, setCentreSelected] = useState<ModeleCentre[]>(props.centreSelected ?? [])
  const [sourceSelected, setSourceSelected] = useState<ModeleSource[]>(props.sourceSelected ?? [])
  const [provenanceSelected, setProvenanceSelected] = useState<ModeleProvenance[]>(
    props.provenanceSelected ?? []
  )
  const [equipeSelected, setEquipeSelected] = useState<ModeleEquipe[]>(props.equipeSelected ?? [])
  const [loading, setLoading] = useState(false)
  const [photo, setPhoto] = useState<string | undefined>(
    props.data?.photoProfil ? `${pdp_url}${props.data.photoProfil}` : undefined
  )
  const [fileName, setFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [centres, setCentres] = useState<Array<ModeleEditCentre>>([])
  const [sources, setSources] = useState<Array<ModeleEditSource>>([])
  const [provenances, setProvenances] = useState<Array<ModeleEditProvenance>>([])
  const [equipes, setEquipes] = useState<Array<ModeleEditEquipe>>([])
  const [roles, setRoles] = useState<Array<ModeleEditRole>>([])
  const [serviceRattachements, setServiceRattachements] = useState<Array<ModeleValueLabel>>([])
  const [currentPage] = useState<number>(1)
  const [searchPage] = useState<string>('')

  /* ------------------------------ Chargements ------------------------------ */

  useEffect(() => {
    getCentres(currentPage, searchPage)
      .then((res) => {
        if (res?.data !== undefined) setCentres(toOptions(res.data, 'id', 'nom'))
      })
      .catch((e) => console.error('erreur centres: ', e))
  }, [currentPage, searchPage])

  // Ces listes ne dépendent de rien : chargées une seule fois
  useEffect(() => {
    getRoles()
      .then((res) => {
        if (res?.data !== undefined) setRoles(toOptions(res.data, 'id', 'libelle'))
      })
      .catch((e) => console.error('erreur rôles: ', e))

    getAllServiceRattachements()
      .then((res) => {
        if (res?.data !== undefined) setServiceRattachements(toOptions(res.data, 'id', 'libelle'))
      })
      .catch((e) => console.error('erreur services: ', e))

    getSources()
      .then((res) => {
        if (res?.data !== undefined && res?.status === 200)
          setSources(toOptions(res.data, 'code', 'libelle'))
      })
      .catch((e) => console.error('erreur sources: ', e))

    getProvenancePiece(false)
      .then((res) => {
        if (res?.data !== undefined && res?.status === 200)
          setProvenances(toOptions(res.data, 'id', 'libelle'))
      })
      .catch((e) => console.error('erreur provenances: ', e))

    getEquipeUtilisateur()
      .then((res) => {
        if (res?.data !== undefined && res?.status === 200)
          setEquipes(toOptions(res.data, 'id', 'nom'))
      })
      .catch((e) => console.error('erreur équipes: ', e))
  }, [])

  // Libération de l'URL blob de la photo (uniquement pour les aperçus locaux)
  useEffect(() => {
    return () => {
      if (photo && photo.startsWith('blob:')) URL.revokeObjectURL(photo)
    }
  }, [photo])

  /* --------------------- Vérification email / pseudo (API) ------------------ */

  // Cache de promesses : une même valeur n'est vérifiée qu'une seule fois,
  // même si plusieurs validations se déclenchent en parallèle.
  const existsCache = useRef(new Map<string, Promise<boolean>>())

  const checkExists = useCallback((key: string, request: () => Promise<any>): Promise<boolean> => {
    const cache = existsCache.current
    if (!cache.has(key)) {
      cache.set(
        key,
        request()
          .then((res) => !!res?.data?.exists)
          .catch((error) => {
            console.error(error)
            cache.delete(key) // on ne garde pas une erreur en cache
            return false // en cas d'erreur réseau, on ne bloque pas l'utilisateur
          })
      )
    }
    return cache.get(key) as Promise<boolean>
  }, [])

  const validationSchema = useMemo(() => {
    const initialEmail = data?.email
    const initialPseudo = data?.pseudo

    const emailUniqueTest = async (value?: string) => {
      // Pas d'appel API si vide, format invalide, ou inchangé en édition
      if (!value || !emailFormatSchema.isValidSync(value)) return true
      if (isEdit && value === initialEmail) return true
      const exists = await checkExists(`email:${value}`, () =>
        isEdit ? validatePseudoEmailEdit(value, '', utilisateurId) : validatePseudoEmail(value, null)
      )
      return !exists
    }

    const pseudoUniqueTest = async (value?: string) => {
      if (!value) return true
      if (isEdit && value === initialPseudo) return true
      const exists = await checkExists(`pseudo:${value}`, () =>
        isEdit ? validatePseudoEmailEdit('', value, utilisateurId) : validatePseudoEmail(null, value)
      )
      return !exists
    }

    const email = Yup.string()
      .required(MSG.emailRequis)
      .email(MSG.emailInvalide)
      .test('email-check', MSG.emailExiste, emailUniqueTest)

    const pseudo = Yup.string()
      .required(MSG.pseudoRequis)
      .test('pseudo-check', MSG.pseudoExiste, pseudoUniqueTest)

    if (isEdit) {
      return Yup.object().shape({
        nom: Yup.string().required(MSG.nomRequis),
        prenom: Yup.string().required(MSG.prenomRequis),
        email,
        pseudo,
        password: Yup.string().notRequired().min(8, MSG.mdpMin),
        passwordConfirmation: Yup.string().when('password', {
          is: (password?: string) => !!password && password.length > 0,
          then: () =>
            Yup.string()
              .required(MSG.confirmationRequise)
              .oneOf([Yup.ref('password')], MSG.confirmationDifferente),
          otherwise: () => Yup.string().notRequired(),
        }),
      })
    }

    return Yup.object().shape({
      nom: Yup.string().required(MSG.nomRequis),
      prenom: Yup.string().required(MSG.prenomRequis),
      email,
      pseudo,
      password: Yup.string().required(MSG.mdpRequis).min(8, MSG.mdpMin),
      passwordConfirmation: Yup.string()
        .required(MSG.confirmationRequise)
        .oneOf([Yup.ref('password')], MSG.confirmationDifferente),
    })
  }, [isEdit, utilisateurId, data?.email, data?.pseudo, checkExists])

  /* --------------------------------- Formik --------------------------------- */

  const formik = useFormik<ModeleUtilisateur>({
    initialValues: data,
    validationSchema,
    // Pas de validation globale au blur/change : elle relançait les tests async
    // (donc les appels API) de tous les champs. On valide champ par champ ci-dessous.
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values) => {
      setLoading(true)
      // On synchronise les sélections multiples au moment de l'envoi
      const updatedData = {
        ...data,
        ...values,
        u_centre: centreSelected.map((o: any) => o.value),
        sources: sourceSelected.map((o: any) => o.value),
        provenance: provenanceSelected.map((o: any) => o.value),
        equipes: equipeSelected.map((o: any) => o.value),
      }
      props.submitForm(updatedData, () => {
        existsCache.current.clear() // les données ont changé côté serveur
        formik.resetForm()
        setLoading(false)
      })
    },
  })

  const handleChangeSel = (
    sel: 'sources' | 'u_centre' | 'provenance' | 'equipes',
    option: readonly any[] | null
  ) => {
    const selected = option ? [...option] : []
    const selectedValues = selected.map((opt) => opt.value)
    formik.setFieldValue(sel, selectedValues, false)

    if (sel === 'sources') setSourceSelected(selected)
    if (sel === 'u_centre') setCentreSelected(selected)
    if (sel === 'provenance') setProvenanceSelected(selected)
    if (sel === 'equipes') setEquipeSelected(selected)
  }

  /* ---------------------------- Photo de profil ----------------------------- */

  const handleButtonClick = () => fileInputRef.current?.click()

  const filePdpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filePdp = event.target.files ? event.target.files[0] : null
    if (!filePdp) {
      setFileName(null)
      return
    }

    setFileName(filePdp.name)
    setPhoto(URL.createObjectURL(filePdp))

    const formData = new FormData()
    formData.append('photoProfil', filePdp, filePdp.name)

    axios
      .post(REGISTER_PDP_URL, formData, {headers: {'Content-Type': 'multipart/form-data'}})
      .then((response) => {
        formik.setFieldValue('photoProfil', response.data.fileName, false)
      })
      .catch((error) => {
        formik.setFieldValue('photoProfil', '', false)
        Swal.fire({
          title: 'Error',
          text: error?.response?.data?.message || 'Erreur inconnue du serveur.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#1BC5BD',
        })
      })
  }

  /* --------------------------------- Rendu ---------------------------------- */

  return (
    <div>
      <form onSubmit={formik.handleSubmit} noValidate className='form'>
        <div className='card-body border-top p-9'>
          <div className='row'>
            <div className='col-lg-8 col-sm-12'>
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Nom complet</label>
                <div className='col-lg-8'>
                  <div className='row'>
                    <div className='col-lg-6 fv-row'>
                      <TextInput
                        fieldName={'nom'}
                        inputPlaceholder={'Nom'}
                        inputType={'text'}
                        inputClassName={INPUT_CLASS}
                        // onBlur={handleBlurWithValidation}
                        formik={formik}
                      />
                    </div>
                    <div className='col-lg-6 fv-row'>
                      <TextInput
                        fieldName={'prenom'}
                        inputPlaceholder={'Prénom'}
                        inputType={'text'}
                        inputClassName={INPUT_CLASS}
                        // onBlur={handleBlurWithValidation}
                        formik={formik}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                  Identification
                </label>
                <div className='col-lg-8'>
                  <div className='row'>
                    <div className='col-lg-6 fv-row'>
                      <TextInput
                        fieldName={'email'}
                        inputPlaceholder={'Email'}
                        inputType={'text'}
                        inputClassName={INPUT_CLASS}
                        onChange={formik.handleChange}
                        // onBlur={handleBlurWithValidation}
                        formik={formik}
                      />
                    </div>
                    <div className='col-lg-6 fv-row'>
                      <TextInput
                        fieldName={'pseudo'}
                        inputPlaceholder={"Nom d'utilisateur"}
                        inputType={'text'}
                        inputClassName={INPUT_CLASS}
                        onChange={formik.handleChange}
                        // onBlur={handleBlurWithValidation}
                        formik={formik}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <PasswordField
                label='Mot de passe'
                name='password'
                placeholder='Mot de passe'
                required={!isEdit}
                formik={formik}
                onBlur={formik.handleBlur}
              />

              <PasswordField
                label='Confirmation mot de passe'
                name='passwordConfirmation'
                placeholder='Confirmation mot de passe'
                required={!isEdit}
                formik={formik}
                onBlur={formik.handleBlur}
              />

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                  Rôle utilisateur
                </label>
                <div className='col-lg-8 fv-row'>
                  <SelectInput
                    fieldName={'idRole'}
                    inputClassName={'form-select form-select-solid form-select-lg fw-bold'}
                    options={roles}
                    formik={formik}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>
                  Service de rattachement
                </label>
                <div className='col-lg-8 fv-row'>
                  <SelectInput
                    fieldName={'idServiceRattachement'}
                    inputClassName={'form-select form-select-solid form-select-lg fw-bold'}
                    options={serviceRattachements}
                    formik={formik}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>Centre</label>
                <div className='col-lg-8 fv-row'>
                  <Select
                    className={SELECT_CLASS}
                    classNamePrefix='react-select'
                    options={centres}
                    name='u_centre'
                    placeholder='Sélectionner centres'
                    isMulti
                    value={centreSelected}
                    onChange={(e) => handleChangeSel('u_centre', e)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>Sources</label>
                <div className='col-lg-8 fv-row'>
                  <Select
                    className={SELECT_CLASS}
                    classNamePrefix='react-select'
                    options={sources}
                    name='sources'
                    placeholder='Sélectionner sources'
                    isMulti
                    value={sourceSelected}
                    onChange={(e) => handleChangeSel('sources', e)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>Provenances</label>
                <div className='col-lg-8 fv-row'>
                  <Select
                    className={SELECT_CLASS}
                    classNamePrefix='react-select'
                    options={provenances}
                    name='provenance'
                    placeholder='Sélectionner provenances'
                    isMulti
                    value={provenanceSelected}
                    onChange={(e) => handleChangeSel('provenance', e)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>Équipes</label>
                <div className='col-lg-8 fv-row'>
                  <Select
                    className={SELECT_CLASS}
                    classNamePrefix='react-select'
                    options={equipes}
                    name='equipes'
                    placeholder='Sélectionner équipes'
                    isMulti
                    value={equipeSelected}
                    onChange={(e) => handleChangeSel('equipes', e)}
                  />
                </div>
              </div>
            </div>

            <div className='col-lg-4 col-sm-12'>
              <div className='input-group'>
                <input
                  ref={fileInputRef}
                  type='file'
                  name='photo-profil'
                  accept='.png, .jpg, .jpeg'
                  style={{display: 'none'}}
                  onChange={filePdpChange}
                />
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={handleButtonClick}
                  style={{borderRadius: '4px 0 0 4px'}}
                >
                  Choisir un fichier
                </button>
                <input
                  type='text'
                  className='form-control'
                  value={fileName ?? 'Insérer votre fichier ici'}
                  readOnly
                />
              </div>

              {photo && (
                <div className='pdp-image d-flex justify-content-lg-center mt-10'>
                  <img
                    src={photo}
                    alt='Photo de profil'
                    style={{
                      width: '150px',
                      height: '150px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      marginTop: '10px',
                      boxShadow: '0 2px 6px #666',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button
            type='button'
            className='btn btn-light me-3'
            disabled={loading}
            onClick={() => {
              formik.resetForm()
              navigate('/utilisateurs')
            }}
          >
            Annuler
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            disabled={loading || !formik.isValid}
          >
            {!loading && 'Enregistrer'}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Enregistrement...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export {UtilisateurForm}
